// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

// eslint-disable-next-line spire/fenced-imports
import { hideAll, Instance as TippyInstance } from "tippy.js";

/* eslint-disable spire/export-styles */
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import { BrandModel } from "@insite/client-framework/Types/ApiModels";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import ChevronDown from "@insite/mobius/Icons/ChevronDown";
import LazyImage from "@insite/mobius/LazyImage";
import Link, { StyledIcon } from "@insite/mobius/Link";
import * as ariaAppHider from "@insite/mobius/Overlay/helpers/ariaAppHider";
import Typography from "@insite/mobius/Typography";
import FancyScroll from "@pace/App/Components/Common/FancyScroll";
import LazyTippy from "@pace/App/Components/Common/LazyTippy";
import TitleTypography from "@pace/App/Components/Common/TitleTypography";
import { HeaderHandlerKey } from "@pace/App/Handlers/XNG/Header";
import { InnerMenuItemTrigger, MenuItemTrigger } from "@pace/App/WidgetExtensions/Header/MainNavigationExtensions";
import Clickable from "@pace/MobiusOverrides/Clickable/Clickable";
import Menu from "@pace/MobiusOverrides/Menu";
import { MainNavigationStyles, MappedLink } from "@pace/Overrides/Widgets/Header/MainNavigation";
import IconPaginationArrowRight from "@pace/XDL/Icons/IconPaginationArrowRight";
import { addScrollbarGutter, isTouchDevice, removeScrollbarGutter } from "@pace/XDL/Utilities";
import * as React from "react";
import { ThemeProps, withTheme } from "styled-components";

interface OwnProps {
    link: MappedLink;
    index: number;
    styles: MainNavigationStyles;
    container: React.RefObject<HTMLElement>;
    isOpen: boolean;
    displayChangeCustomerLink: boolean;
    // XNG-Changes
    brandsList?: BrandModel[];
    brandPageUrl?: string;
    xHeaderUpdate: (params: { key?: HeaderHandlerKey; value: any }) => void;
}

type Props = ThemeProps<BaseTheme> & OwnProps;

/**
 * XNG-Changes
 */
const hideOnPopperBlurPlugin = {
    name: "hideOnPopperBlur",
    defaultValue: true,
    fn(instance: TippyInstance) {
        return {
            onCreate() {
                instance.popper.addEventListener("focusout", event => {
                    if (
                        event.relatedTarget &&
                        event.relatedTarget instanceof HTMLElement &&
                        !instance.popper.contains(event.relatedTarget)
                    ) {
                        instance.hide();
                    }
                });
            },
        };
    },
};

class MainNavigationItem extends React.Component<Props> {
    element = React.createRef<HTMLElement>();
    popover = React.createRef<any>();

    componentDidUpdate(prevProps: Readonly<Props>) {
        // if (this.props.isOpen && !prevProps.isOpen) {
        //     this.openPopover();
        // } else if (!this.props.isOpen && prevProps.isOpen) {
        //     this.closePopover();
        // }
    }

    // closePopover = () => {
    //     this.popover.current && (this.popover.current! as any).closePopover();
    // };

    // openPopover = () => {
    //     this.popover.current && (this.popover.current! as any).openPopover();
    // };

    // <XNG-Changes>
    renderMegaMenu = (link: MappedLink) => {
        const parentMenu = React.createRef<HTMLDivElement>();

        return (
            <StyledWrapper {...this.props.styles.megaMenuFirstColumn} id="mega-menu-first-column">
                <StyledWrapper {...this.props.styles.megaMenuLevel1}>
                    <FancyScroll refElement={parentMenu}>
                        <>
                            {link
                                .children!.filter(child => !child.excludeFromNavigation)
                                .map((child, index) => {
                                    const link = (
                                        <Link
                                            className="mega-menu-heading-link"
                                            {...this.props.styles.megaMenuLink}
                                            href={child.url}
                                            key={index}
                                        >
                                            <Typography>{child.title}</Typography>
                                            {child.children && child.children?.length > 0 && (
                                                <StyledIcon src={IconPaginationArrowRight} size={9} color="secondary" />
                                            )}
                                        </Link>
                                    );
                                    const firstColumn = document.getElementById("mega-menu-first-column")!;
                                    return (
                                        <>
                                            {!child.children?.length ? (
                                                link
                                            ) : (
                                                <LazyTippy
                                                    render={() => (
                                                        <>{this.renderMegaMenuSecondColumn(child.children)}</>
                                                    )}
                                                    trigger="mouseenter focusin"
                                                    placement="right-start"
                                                    interactive={true}
                                                    animation={false}
                                                    delay={[120, 350]}
                                                    interactiveBorder={10}
                                                    offset={[0, 0]}
                                                    plugins={[hideOnPopperBlurPlugin]}
                                                    touch={["hold", 250]}
                                                    arrow={false}
                                                    appendTo={() => firstColumn}
                                                    popperOptions={{
                                                        modifiers: [
                                                            {
                                                                name: "computeStyles",
                                                                options: {
                                                                    roundOffsets: ({ x, y }: any) => ({
                                                                        x:
                                                                            firstColumn &&
                                                                            firstColumn.scrollHeight >
                                                                                firstColumn.clientHeight
                                                                                ? 268
                                                                                : 264,
                                                                        y: 0,
                                                                    }),
                                                                },
                                                            },
                                                        ],
                                                    }}
                                                >
                                                    <InnerMenuItemTrigger>{link}</InnerMenuItemTrigger>
                                                </LazyTippy>
                                            )}
                                        </>
                                    );
                                })}
                        </>
                    </FancyScroll>
                </StyledWrapper>
            </StyledWrapper>
        );
    };

    renderMegaMenuSecondColumn = (children: MappedLink[] | undefined) => {
        if (!children) {
            return null;
        }
        return (
            <>
                <StyledWrapper {...this.props.styles.megaMenuSecondColumn}>
                    <StyledWrapper {...this.props.styles.megaMenuLevel2}>
                        {children!
                            .filter(child => !child.excludeFromNavigation)
                            .map((child, index) => (
                                <StyledWrapper key={index} {...this.props.styles.megaMenuLevel2Section}>
                                    <Link
                                        href={child.url}
                                        {...this.props.styles.megaMenuLevel2Link}
                                        target={child.openInNewWindow ? "_blank" : ""}
                                    >
                                        {child.title}
                                    </Link>
                                    {child
                                        .children?.filter(child1 => !child1.excludeFromNavigation)
                                        .map((child1, index1) => (
                                            <Link
                                                key={index1}
                                                href={child1.url}
                                                {...this.props.styles.megaMenuLevel3Link}
                                                target={child1.openInNewWindow ? "_blank" : ""}
                                            >
                                                {child1.title}
                                            </Link>
                                        ))}
                                </StyledWrapper>
                            ))}
                    </StyledWrapper>
                </StyledWrapper>
                {this.props.brandsList && this.props.brandsList.length > 0 &&
                    <StyledWrapper {...this.props.styles.megaMenuBrands}>
                        <TitleTypography
                            link={this.props.brandPageUrl!}
                            linkText={translate("View All Brands")}
                            extendedStyles={this.props.styles.shopBrandsTitle}
                            variant="small"
                        >
                            {translate("Shop Top Brands")}
                        </TitleTypography>
                        <StyledWrapper {...this.props.styles.brandsWrapper}>
                            {this.props.brandsList?.map(brand => (
                                <Clickable href={brand.detailPagePath} {...this.props.styles.brandItem} key={brand.id}>
                                    <LazyImage
                                        width="87"
                                        height="87"
                                        {...this.props.styles.brandImage}
                                        src={brand.logoSmallImagePath}
                                        altText={`${translate("Go to brand page")} ${brand.logoAltText || brand.name}`}
                                    />
                                </Clickable>
                            ))}
                        </StyledWrapper>
                    </StyledWrapper>
                }
            </>
        );
    };
    // </XNG-Changes>

    render() {
        const {
            styles,
            link,
            index,
            isOpen,
            theme: {
                breakpoints: { maxWidths },
            },
        } = this.props;
        // XNG-Changes:
        // add class to arrow
        // add bypassLandingPageClick so a menu item will not have a link
        const downIcon = <StyledIcon className="arrow" src={ChevronDown} {...styles.menuItemIcon} />;
        const hasChildren = link.children && link.children.filter(child => !child.excludeFromNavigation).length > 0;
        const isCascading = hasChildren && link.childrenType === "CascadingMenu";
        const isMega = hasChildren && link.childrenType === "MegaMenu";
        let menuLink;
        if(link.bypassLandingPageClick) {
            menuLink = (
                <Typography
                    className="menu-item"
                    color={styles.menuItemTypography?.color}
                    {...styles.nonClickabkeMenuItem}
                >
                    {link.title}
                    {isCascading && downIcon}
                </Typography>
            );
        } else {
            menuLink = (
                <Link
                    className="menu-item"
                    typographyProps={styles.menuItemTypography}
                    color={styles.menuItemTypography?.color}
                    {...styles.menuItem}
                    href={link.url}
                    target={link.openInNewWindow ? "_blank" : ""}
                >
                    {link.title}
                    {isCascading && downIcon}
                </Link>
            );
        }
        
        if (isMega || link.url === "") {
            menuLink = (
                <Link
                    className="menu-item"
                    typographyProps={styles.menuItemTypography}
                    color={styles.menuItemTypography?.color}
                    {...styles.menuItem}
                    {...(isTouchDevice()
                        ? {
                              onDoubleClick: () => {
                                  window.location.href = link.url;
                              },
                          }
                        : { href: link.url })}
                    aria-label={link.title}
                >
                    {link.title}
                    {(isCascading || isMega) && downIcon}
                </Link>
            );
        }

        let menuItem = menuLink;

        if (isCascading && link.children) {
            const menuProps: { maxDepth?: number; width?: number; hideOnClick?: boolean; menuHeight?: number } = {
                hideOnClick: false,
                menuHeight: link.menuHeight,
            };
            if (link.maxDepth) {
                menuProps.maxDepth = link.maxDepth;
            }

            // XNG-Changes: close menu on click when the item has url
            if (link.url) {
                menuProps.hideOnClick = true;
            }

            menuItem = (
                <Menu
                    className="cascading-menu"
                    data-test-selector={`mainNavigationItem_${index}`}
                    isOpen={isOpen || undefined}
                    descriptionId={`${link.title}_${index}`}
                    menuItems={link.children}
                    menuTrigger={menuLink}
                    displayChangeCustomerLink={this.props.displayChangeCustomerLink}
                    {...menuProps}
                    {...styles.cascadingMenu}
                    // XNG-Change
                    bypassLandingPageClick={link.bypassLandingPageClick}
                />
            );
        }

        if (isMega) {
            menuItem = (
                <LazyTippy
                    render={() => <>{this.renderMegaMenu(link)}</>}
                    trigger="mouseenter focusin"
                    placement="bottom-start"
                    maxWidth={1334}
                    hideOnClick={true}
                    interactive={true}
                    animation={false}
                    delay={[120, 350]}
                    offset={[0, 8]}
                    interactiveBorder={10}
                    plugins={[hideOnPopperBlurPlugin]}
                    appendTo="parent"
                    arrow={false}
                    onShow={instance => {
                        hideAll(); // To close all Tippy instances
                        this.props.xHeaderUpdate({ key: "registerMenu", value: `${instance.id}_menu` });
                        addScrollbarGutter();
                        ariaAppHider.hide(document.body);
                    }}
                    onHidden={instance => {
                        this.props.xHeaderUpdate({ key: "unregisterMenu", value: `${instance.id}_menu` });
                        removeScrollbarGutter();
                        ariaAppHider.show(document.body);
                    }}
                    popperOptions={{
                        strategy: "fixed",
                    }}
                >
                    <MenuItemTrigger role="link">{menuLink}</MenuItemTrigger>
                </LazyTippy>
            );
        }

        return menuItem;
    }
}

export default withTheme(MainNavigationItem);
